import { Button } from "@bcc/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bcc/components/ui/dialog";
import { Label } from "@bcc/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@bcc/components/ui/select";
import { Package, ShoppingBag, Weight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { setCreateBatchDialogTestId } from "./create-batch-dialog.utils";

interface CreateBatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedOrders: string[];
  onSuccess: () => void;
  testId?: string;
}

export function CreateBatchDialog({
  open,
  onOpenChange,
  selectedOrders,
  onSuccess,
  testId,
}: CreateBatchDialogProps) {
  const [carrier, setCarrier] = useState("beanz");
  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleCreateBatch = async () => {
    setIsCreating(true);
    // Simulate batch creation
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsCreating(false);
    onOpenChange(false);
    onSuccess();
    navigate("/batches");
  };

  // Mock calculations
  const totalOrders = selectedOrders.length;
  const totalUnits = totalOrders * 3; // Average 3 bags per order
  const totalWeight = totalUnits * 12; // 12 oz per bag

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        testId={testId ?? setCreateBatchDialogTestId("content")}
        className="sm:max-w-[500px]"
      >
        <DialogHeader>
          <DialogTitle>{t("views.createBatchDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("views.createBatchDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Batch Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="flex flex-col items-center gap-2 rounded-lg border border-border bg-muted/50 p-4">
              <Package className="h-5 w-5 text-muted-foreground" />
              <div className="text-center">
                <div className="text-2xl font-bold">{totalOrders}</div>
                <div className="text-xs text-muted-foreground">
                  {t("views.createBatchDialog.orders")}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2 rounded-lg border border-border bg-muted/50 p-4">
              <ShoppingBag className="h-5 w-5 text-muted-foreground" />
              <div className="text-center">
                <div className="text-2xl font-bold">{totalUnits}</div>
                <div className="text-xs text-muted-foreground">
                  {t("views.createBatchDialog.units")}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2 rounded-lg border border-border bg-muted/50 p-4">
              <Weight className="h-5 w-5 text-muted-foreground" />
              <div className="text-center">
                <div className="text-2xl font-bold">{totalWeight}</div>
                <div className="text-xs text-muted-foreground">
                  {t("views.createBatchDialog.ozTotal")}
                </div>
              </div>
            </div>
          </div>

          {/* Carrier Selection */}
          <div className="space-y-2">
            <Label htmlFor="carrier">
              {t("views.createBatchDialog.carrier")}
            </Label>
            <Select value={carrier} onValueChange={setCarrier}>
              <SelectTrigger id="carrier">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="beanz">Beanz.com</SelectItem>
                <SelectItem value="ups">UPS</SelectItem>
                <SelectItem value="usps">USPS</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("views.createBatchDialog.cancel")}
          </Button>
          <Button onClick={handleCreateBatch} disabled={isCreating}>
            {isCreating
              ? t("views.createBatchDialog.creating")
              : t("views.createBatchDialog.createBatch")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
